import type { JSX } from 'react';
import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCompany } from '../hooks/useCompany';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { refereeSettingsSchema, type RefereeSettingsFormData } from '../schemas/company.schemas';

export function RefereeSettingsPage(): JSX.Element {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { company, isLoading } = useCompany(companyId);
  const { updateRefereeSettings, isRefereeSettingsLoading } = useCompanySettings();
  const settingsPath = `/companies/${companyId}/settings`;

  const form = useForm<RefereeSettingsFormData>({
    resolver: zodResolver(refereeSettingsSchema),
    defaultValues: { linkExpirationDays: 30 },
  });

  useEffect(() => {
    const companyWithReferee = company as
      { refereeSettings?: { linkExpirationDays: number } } | undefined;
    if (companyWithReferee?.refereeSettings) {
      form.reset({ linkExpirationDays: companyWithReferee.refereeSettings.linkExpirationDays });
    }
  }, [company, form]);

  const onSubmit = (data: RefereeSettingsFormData): void => {
    if (!companyId) return;
    updateRefereeSettings(
      { id: companyId, settings: { linkExpirationDays: data.linkExpirationDays } },
      {
        onSuccess: () => {
          toast.success('Reference contact settings updated successfully');
          navigate(settingsPath);
        },
        onError: () => toast.error('Failed to update reference contact settings'),
      }
    );
  };

  if (isLoading) {
    return (
      <div className="container py-6 flex items-center justify-center min-h-[400px]">
        <span className="text-muted-foreground">Loading...</span>
      </div>
    );
  }

  return (
    <div className="container py-6 space-y-6">
      <Link
        to={settingsPath}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Settings
      </Link>
      <div>
        <h1 className="text-2xl font-bold">Reference contact settings</h1>
        <p className="text-muted-foreground">Configure reference contact share link settings</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Share Link Expiry</CardTitle>
          <CardDescription>Set how long reference contact share links remain valid</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="linkExpirationDays">Link Expiration (days)</Label>
              <Input
                id="linkExpirationDays"
                type="number"
                min={1}
                max={365}
                {...form.register('linkExpirationDays', { valueAsNumber: true })}
              />
              {form.formState.errors.linkExpirationDays && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.linkExpirationDays.message}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-4">
              <Link to={settingsPath}>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </Link>
              <Button type="submit" disabled={isRefereeSettingsLoading}>
                {isRefereeSettingsLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
